import { useState, useEffect, useMemo } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  GripVertical, Phone, Mail, MapPin, Calendar, DollarSign,
  ChevronRight, MessageSquare, Settings, X, Filter, Save
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  new: 'bg-blue-50 border-blue-200 text-blue-800',
  contacted: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  quoted: 'bg-purple-50 border-purple-200 text-purple-800',
  negotiation: 'bg-amber-50 border-amber-200 text-amber-800',
  converted: 'bg-green-50 border-green-200 text-green-800',
  lost: 'bg-red-50 border-red-200 text-red-800',
};

const SOURCE_LABELS = {
  enquiry: 'Enquiry',
  contact: 'Contact',
  lead: 'Get Quote',
  wedding: 'Wedding',
  corporate: 'Corporate',
};

const SOURCE_COLORS = {
  enquiry: 'bg-blue-100 text-blue-700',
  contact: 'bg-slate-100 text-slate-700',
  lead: 'bg-emerald-100 text-emerald-700',
  wedding: 'bg-pink-100 text-pink-700',
  corporate: 'bg-violet-100 text-violet-700',
};

export default function AdminLeadPipeline() {
  const { token } = useAdmin();
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedLead, setDraggedLead] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const loadLeads = async () => {
    const { data } = await axios.get(`${API}/admin/pipeline/leads`, { headers });
    setLeads(data);
  };

  const loadSettings = async () => {
    const { data } = await axios.get(`${API}/admin/pipeline/settings`, { headers });
    setSettings(data);
  };

  useEffect(() => { loadLeads(); loadSettings(); }, []);

  const stages = settings?.stages || ['new', 'contacted', 'quoted', 'negotiation', 'converted', 'lost'];

  const filteredLeads = useMemo(() => {
    let result = leads;
    if (sourceFilter !== 'all') result = result.filter(l => l.source === sourceFilter);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(l => l.name?.toLowerCase().includes(s) || l.email?.toLowerCase().includes(s) || l.phone?.includes(s));
    }
    return result;
  }, [leads, sourceFilter, searchTerm]);

  const leadsByStage = useMemo(() => {
    const grouped = {};
    stages.forEach(s => { grouped[s] = []; });
    filteredLeads.forEach(l => {
      const stage = stages.includes(l.status) ? l.status : 'new';
      if (grouped[stage]) grouped[stage].push(l);
    });
    return grouped;
  }, [filteredLeads, stages]);

  const moveLeadToStage = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/admin/pipeline/leads/${leadId}/status`, { status: newStage }, { headers });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStage } : l));
      toast.success(`Moved to ${newStage}`);
    } catch { toast.error('Failed to update'); }
  };

  const saveNote = async () => {
    if (!selectedLead) return;
    await axios.put(`${API}/admin/pipeline/leads/${selectedLead.id}/notes`, { notes: noteText }, { headers });
    setLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, notes: noteText } : l));
    toast.success('Notes saved');
  };

  const saveSettings = async () => {
    await axios.put(`${API}/admin/pipeline/settings`, settings, { headers });
    toast.success('Pipeline settings saved');
    setShowSettings(false);
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const handleDrop = (e, stage) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== stage) {
      moveLeadToStage(draggedLead.id, stage);
    }
    setDraggedLead(null);
  };

  return (
    <div className="space-y-4" data-testid="admin-lead-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lead Pipeline</h1>
          <p className="text-slate-500 text-sm mt-1">Drag & drop leads across stages to track progress</p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="pipeline-settings-btn">
          <Settings className="w-4 h-4 mr-2" /> Settings
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input placeholder="Search leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-64" data-testid="pipeline-search" />
        <div className="flex gap-1.5">
          {['all', ...Object.keys(SOURCE_LABELS)].map(s => (
            <Button key={s} variant={sourceFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setSourceFilter(s)}>
              {s === 'all' ? 'All' : SOURCE_LABELS[s]}
            </Button>
          ))}
        </div>
        <span className="text-sm text-slate-500 ml-auto">{filteredLeads.length} leads</span>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
        {stages.map(stage => (
          <div
            key={stage}
            className="flex-shrink-0 w-72"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage)}
            data-testid={`pipeline-stage-${stage}`}
          >
            <div className={`rounded-t-lg px-3 py-2 border ${STAGE_COLORS[stage] || 'bg-slate-50 border-slate-200 text-slate-800'} flex items-center justify-between`}>
              <span className="font-semibold text-sm capitalize">{stage.replace('_', ' ')}</span>
              <span className="text-xs font-bold bg-white/60 rounded-full px-2 py-0.5">{leadsByStage[stage]?.length || 0}</span>
            </div>
            <div className="bg-slate-50/50 border border-t-0 border-slate-200 rounded-b-lg p-2 space-y-2 min-h-[200px]">
              {(leadsByStage[stage] || []).map(lead => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead)}
                  onClick={() => { setSelectedLead(lead); setNoteText(lead.notes || ''); }}
                  className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  data-testid={`lead-card-${lead.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-medium text-sm text-slate-800 truncate flex-1">{lead.name || 'Unknown'}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${SOURCE_COLORS[lead.source] || 'bg-slate-100'}`}>
                      {SOURCE_LABELS[lead.source] || lead.source}
                    </span>
                  </div>
                  {lead.email && <p className="text-xs text-slate-500 truncate">{lead.email}</p>}
                  {lead.destination && <p className="text-xs text-slate-400 mt-1 truncate">{typeof lead.destination === 'string' ? lead.destination : JSON.stringify(lead.destination)}</p>}
                  <p className="text-[10px] text-slate-400 mt-2">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : ''}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">{selectedLead.name}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setSelectedLead(null)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                {selectedLead.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {selectedLead.email}</p>}
                {selectedLead.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {selectedLead.phone}</p>}
                {selectedLead.destination && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {typeof selectedLead.destination === 'string' ? selectedLead.destination : JSON.stringify(selectedLead.destination)}</p>}
                {selectedLead.budget && <p className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-slate-400" /> {selectedLead.budget}</p>}
                {selectedLead.travel_date && <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /> {selectedLead.travel_date}</p>}
              </div>
              {selectedLead.message && <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{selectedLead.message}</p>}
              <div>
                <Label>Move to Stage</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                  value={selectedLead.status}
                  onChange={(e) => { moveLeadToStage(selectedLead.id, e.target.value); setSelectedLead({ ...selectedLead, status: e.target.value }); }}
                  data-testid="lead-stage-select"
                >
                  {stages.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="Add internal notes..." data-testid="lead-notes" />
                <Button size="sm" className="mt-2" onClick={saveNote}><Save className="w-3 h-3 mr-1" /> Save Notes</Button>
              </div>
              <p className="text-xs text-slate-400">Source: {SOURCE_LABELS[selectedLead.source]} • Created: {selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleString() : 'N/A'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && settings && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowSettings(false)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pipeline Settings</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowSettings(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Pipeline Stages (comma-separated)</Label>
                <Input
                  value={settings.stages?.join(', ')}
                  onChange={(e) => setSettings({ ...settings, stages: e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) })}
                  data-testid="pipeline-stages-input"
                />
                <p className="text-xs text-slate-500 mt-1">Define your custom pipeline stages</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={settings.auto_followup_enabled} onCheckedChange={(v) => setSettings({ ...settings, auto_followup_enabled: v })} />
                <Label>Auto Follow-up Email</Label>
              </div>
              <div><Label>Follow-up Email Subject</Label><Input value={settings.followup_email_subject || ''} onChange={(e) => setSettings({ ...settings, followup_email_subject: e.target.value })} data-testid="followup-subject" /></div>
              <div><Label>Follow-up Email Body (HTML, use {'{name}'} for customer name)</Label><Textarea value={settings.followup_email_body || ''} onChange={(e) => setSettings({ ...settings, followup_email_body: e.target.value })} rows={8} className="font-mono text-xs" data-testid="followup-body" /></div>
              <Button className="w-full" onClick={saveSettings} data-testid="save-pipeline-settings-btn">Save Settings</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
